// useTestimonials — live-subscribed list of published testimonials.
// Falls back to SEED_TESTIMONIALS while no real reviews exist yet so the
// public site's testimonial reel always has something warm to show.
//
// Each returned item may carry an attached `pancake` field (id, title,
// eventName, thumbUrl, cutoutUrl) joined from `config/publicGallery` by
// orderId, so callers can surface the actual pancake the guest is talking
// about — and derive the venue caption from the shift's eventName rather than
// anything the guest typed. Seeds have no orderId and so always have
// pancake = null (they fall back to their hand-written context).

import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { SEED_TESTIMONIALS, orderedTestimonials } from "../utils/testimonials";

export function useTestimonials() {
  const [published, setPublished] = useState(null); // null = not loaded yet
  const [galleryMap, setGalleryMap] = useState({}); // orderId → pancake
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "testimonials"), where("status", "==", "published"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPublished(rows);
      },
      (err) => {
        console.warn("useTestimonials:", err.message);
        setError(err);
        setPublished([]);
      }
    );
    return unsub;
  }, []);

  // Pull publicGallery to join pancake imagery onto testimonials. Quiet on
  // failure — if the doc isn't readable we just lose the imagery, not the
  // quote.
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config/publicGallery"),
      (s) => {
        const items = s.exists() ? (s.data()?.items || []) : [];
        const map = {};
        for (const p of items) {
          if (!p?.id) continue;
          map[p.id] = {
            id: p.id,
            title: p.title || null,
            eventName: p.eventName || null, // shift/event name — drives the venue caption
            thumbUrl: p.thumbUrl || null,
            cutoutUrl: p.cutoutUrl || null,
          };
        }
        setGalleryMap(map);
      },
      () => setGalleryMap({}),
    );
    return unsub;
  }, []);

  // While loading, show seeds so the page isn't empty.
  // After load: if zero published, still show seeds. Once any real review
  // publishes, switch entirely to real.
  const source = (published == null || published.length === 0) ? SEED_TESTIMONIALS : published;
  const withPancakes = source.map((t) => ({
    ...t,
    pancake: t.orderId ? (galleryMap[t.orderId] || null) : null,
  }));
  return {
    items: orderedTestimonials(withPancakes),
    loading: published == null,
    error,
    usingSeeds: (published == null || published.length === 0),
  };
}
